import type { DirectoryUser, MuqriRequest } from '../firebase/admin';
import type { OpenSession } from '../firebase/sessions';

export type AdminStats = {
  users: number;
  muqris: number;
  admins: number;
  pendingRequests: number;
  liveSessions: number;
  studentsWaiting: number;
};

/**
 * The admin dashboard's numbers, derived rather than stored.
 *
 * Pure and separate from the screen so the arithmetic can be tested without a
 * renderer — and counted from the SAME live collections the other tabs read,
 * so a number here can never disagree with the list one tab over. A stored
 * counter would be a second source of truth that drifts the first time a
 * write fails halfway.
 */
export function summarize(
  users: DirectoryUser[],
  requests: MuqriRequest[],
  sessions: OpenSession[]
): AdminStats {
  return {
    users: users.length,
    muqris: users.filter((u) => u.isMuqri).length,
    admins: users.filter((u) => u.isAdmin).length,
    // Counted, not taken as given: listenToPendingRequests already filters on
    // the server, but a caller passing the unfiltered list would otherwise
    // silently report decided requests as pending.
    pendingRequests: requests.filter((r) => r.status === 'pending').length,
    liveSessions: sessions.length,
    // A negative or non-numeric waitingCount would poison the total. The
    // field is server-owned, but this is a sum shown to a human, and one bad
    // document should cost its own row rather than the whole figure.
    studentsWaiting: sessions.reduce(
      (total, s) => total + (Number.isFinite(s.waitingCount) && s.waitingCount > 0 ? s.waitingCount : 0),
      0
    ),
  };
}
